// @flow
// $FlowFixMe
import React, { useMemo, Fragment } from "react";
import { get, noop, camelCase } from "lodash";
import type {
  HelpDescription,
  HelpModalTypeEnum,
  HelpBetDescription,
  HelpTutorialDescription,
  WinProbabilityFDR
} from "@fdr/types/ProgramPage";
import Bubble1 from "@fdr/static-ui/SvgImages/Bubble1";
import Bubble2 from "@fdr/static-ui/SvgImages/Bubble2";
import Bubble3 from "@fdr/static-ui/SvgImages/Bubble3";
import BetTutorial from "@fdr/atomic-ui/_templates/BetTutorial";
import Icon from "@fdr/static-ui/Icons";
import { arrowRight } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import type { NullaryFn } from "@tvg/types/Functional";
import type { StoryblokModalContent } from "@fdr/tutorials/src/types";
import {
  ImageWrapper,
  DescriptionContainer,
  Description,
  TipContainer,
  TipTitle,
  Tip,
  TutorialList,
  ListItem,
  ListNumber,
  ListDescription,
  BetInfoList,
  NotApplicableContainer
} from "./styled-components";
import { FooterWrapper } from "../EducationalHub/styled-components";

type Props = {
  message:
    | HelpDescription
    | HelpBetDescription
    | HelpTutorialDescription
    | WinProbabilityFDR,
  type: HelpModalTypeEnum,
  hasNotApplicableDesc?: boolean,
  contentData?: StoryblokModalContent,
  isLoading?: boolean,
  onBetModalFooterClick?: NullaryFn<void>
};

const Images = [<Bubble1 />, <Bubble2 />, <Bubble3 />];

const HelpModal = (props: Props) => {
  const {
    message,
    type,
    hasNotApplicableDesc,
    contentData,
    isLoading,
    onBetModalFooterClick = noop
  } = props;

  const betTypes = message.typesList
    ? Object.values(message.typesList).map((betType) => get(betType, "title"))
    : [];

  const renderHelpModal = () => (
    <Fragment>
      {get(message, "image") !== null &&
        get(message, "image") !== undefined && (
          <ImageWrapper>{get(message, "image")}</ImageWrapper>
        )}
      <DescriptionContainer>
        <Description
          dangerouslySetInnerHTML={{
            __html: get(message, "description")
          }}
        />
        {hasNotApplicableDesc && get(message, "notApplicable", "") !== "" && (
          <NotApplicableContainer>
            <Description>{get(message, "notApplicable")}</Description>
          </NotApplicableContainer>
        )}
        {get(message, "tip", "") !== "" && (
          <TipContainer>
            <TipTitle>TIP</TipTitle>
            <Tip>{get(message, "tip")}</Tip>
          </TipContainer>
        )}
      </DescriptionContainer>
    </Fragment>
  );

  const renderBetModal = () =>
    !isLoading ? (
      <React.Fragment>
        <BetInfoList>
          {get(contentData, "sections", []).map(
            (section) =>
              section.type === "bet_types" &&
              get(message, "typesList.length", 0) > 0 &&
              betTypes !== null && (
                <BetTutorial
                  key={`betTutorial-${camelCase(betTypes.join(","))}`}
                  betCategories={section?.content}
                  bettingType={betTypes}
                />
              )
          )}
        </BetInfoList>

        <FooterWrapper role="button" onClick={onBetModalFooterClick}>
          {get(
            contentData,
            "racecard_footer_title",
            "Want help? Visit our guide"
          )}
          <Icon icon={arrowRight} color={buildColor("blue")} size={17} />
        </FooterWrapper>
      </React.Fragment>
    ) : null;

  const tutorialListItem = (number: number) => (
    <ListItem data-qa-label="modal-tutorial-list-item">
      <ListNumber data-qa-label="modal-tutorial-list-item-number">
        {Images[number - 1]}
      </ListNumber>
      <ListDescription data-qa-label="modal-tutorial-list-item-description">
        {get(message, `description${number}`)}
      </ListDescription>
    </ListItem>
  );

  const renderTutorialModal = () => (
    <TutorialList data-qa-label="modal-tutorial-list-container">
      {tutorialListItem(1)}
      {tutorialListItem(2)}
      {tutorialListItem(3)}
    </TutorialList>
  );

  return useMemo(() => {
    switch (type) {
      case "help": {
        return renderHelpModal();
      }
      case "tutorial": {
        return renderTutorialModal();
      }
      case "betTypes": {
        return renderBetModal();
      }
      default: {
        return renderHelpModal;
      }
    }
  });
};

HelpModal.defaultProps = {
  message: {
    title: "",
    description: "",
    image: null,
    tip: ""
  },
  type: "help",
  hasNotApplicableDesc: false
};

export default HelpModal;
